import React, { useEffect, useMemo, useState } from "react";
import { getLocale } from "../lang/useLanguage";

const API = `${window.location.protocol}//${window.location.hostname}/api`;

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const S = {
  page: { color: "#e5edf7" },
  titleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  title: { margin: 0, fontSize: 26, fontWeight: 900 },
  sub: { color: "#7b8da8", fontSize: 13, marginTop: 6 },
  btn: { background: "#1d4ed8", color: "#fff", border: "1px solid #2563eb", borderRadius: 10, padding: "10px 14px", fontWeight: 900, cursor: "pointer" },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 18 },
  card: { background: "#0f1926", border: "1px solid #1e3a5f", borderRadius: 16, padding: 18 },
  kpiValue: { fontSize: 30, fontWeight: 900, marginTop: 8 },
  kpiLabel: { color: "#7b8da8", fontSize: 12, fontWeight: 800, textTransform: "uppercase" },
  layout: { display: "grid", gridTemplateColumns: "1.35fr 0.95fr", gap: 18, alignItems: "start" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 },
  input: { width: "100%", background: "#08111f", border: "1px solid #1e3a5f", borderRadius: 10, padding: "10px 12px", color: "#e5edf7", outline: "none" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "12px 10px", textAlign: "left", color: "#cbd5e1", fontSize: 12, background: "#1f2937", whiteSpace: "nowrap" },
  td: { padding: "12px 10px", borderBottom: "1px solid #172033", fontSize: 13, verticalAlign: "middle" },
  row: selected => ({ cursor: "pointer", background: selected ? "#102a4a" : "transparent" }),
  pill: online => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 900,
    background: online ? "rgba(16,185,129,.13)" : "rgba(239,68,68,.13)",
    color: online ? "#86efac" : "#fca5a5",
    border: online ? "1px solid rgba(16,185,129,.45)" : "1px solid rgba(239,68,68,.45)"
  }),
  detailTitle: { fontSize: 22, fontWeight: 900, marginBottom: 4 },
  detailSub: { color: "#94a3b8", fontSize: 13, marginBottom: 18 },
  sectionTitle: { fontSize: 15, fontWeight: 900, margin: "18px 0 10px" },
  kvGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  kv: { background: "#08111f", border: "1px solid #172a46", borderRadius: 12, padding: 12 },
  k: { color: "#7b8da8", fontSize: 11, fontWeight: 900, textTransform: "uppercase", marginBottom: 6 },
  v: { fontWeight: 900, wordBreak: "break-word" },
  commandGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 },
  log: { marginTop: 16, background: "#050b13", border: "1px solid #172a46", borderRadius: 12, padding: 12, color: "#93c5fd", fontSize: 12, minHeight: 90, lineHeight: 1.7 },
  historyRow: { display:"grid", gridTemplateColumns:"1.2fr .8fr .9fr", gap:8, alignItems:"center", padding:"9px 10px", borderBottom:"1px solid #172033", cursor:"pointer", fontSize:12 },
  modalBackdrop: { position:"fixed", inset:0, background:"rgba(0,0,0,.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 },
  modal: { width:"min(760px, 92vw)", maxHeight:"82vh", overflow:"auto", background:"#0f1926", border:"1px solid #1e3a5f", borderRadius:16, padding:18, color:"#e5edf7" },
  pre: { background:"#050b13", border:"1px solid #172a46", borderRadius:12, padding:12, overflow:"auto", fontSize:12, lineHeight:1.6 }
};

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString(getLocale());
}

function isOnline(x) {
  return String(x?.gateway_status || "").toLowerCase() === "online";
}

function fmtUptime(seconds) {
  const n = Number(seconds || 0);
  if (!n) return "—";
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (d > 0) return `${d} gün ${h} saat`;
  if (h > 0) return `${h} saat ${m} dk`;
  return `${m} dk`;
}

function pct(v) {
  return v === null || v === undefined || v === "" ? "—" : `${Number(v).toFixed(1)}%`;
}

function commandStatusStyle(status) {
  const st = String(status || "").toLowerCase();
  if (st === "done") return { color: "#86efac" };
  if (st === "failed") return { color: "#fca5a5" };
  if (st === "pending") return { color: "#93c5fd" };
  if (st === "picked" || st === "running") return { color: "#fde68a" };
  return { color: "#cbd5e1" };
}

function commandLabel(type) {
  const map = {
    restart_edge: "Restart Edge",
    sync_config: "Sync Config",
    test_connection: "Test Connection",
    download_logs: "Download Logs",
    diagnostics: "Diagnostics",
    collect_snapshot: "Snapshot"
  };
  return map[type] || type || "—";
}

function fmtDuration(a, b) {
  if (!a || !b) return "—";
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  return `${(ms / 1000).toFixed(2)} sn`;
}

function jsonText(v) {
  if (!v) return "{}";
  if (typeof v === "string") {
    try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
  }
  return JSON.stringify(v, null, 2);
}

export default function SuperAdminEdgeManager({ tenants = [] }) {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandMsg, setCommandMsg] = useState("");
  const [commands, setCommands] = useState([]);

  const [credentials, setCredentials] = useState([]);
  const [credentialLoading, setCredentialLoading] = useState(false);

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionSaving, setProvisionSaving] = useState(false);
  const [provisionForm, setProvisionForm] = useState({
    tenant_id: "",
    gateway_name: "",
    gateway_code: "",
    location: ""
  });

  const [secretModal, setSecretModal] = useState(null);
  const [selectedCommand, setSelectedCommand] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/superadmin/edge-manager/devices`, { headers: headers() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Edge listesi alınamadı");

      const raw = d.items || [];
      const byTenant = {};
      raw.forEach(x => {
        const key = x.tenant_id || x.tenant_name || x.gateway_id;
        const current = byTenant[key];
        const xt = x.gateway_last_seen ? new Date(x.gateway_last_seen).getTime() : 0;
        const ct = current?.gateway_last_seen ? new Date(current.gateway_last_seen).getTime() : 0;
        if (!current || xt > ct) byTenant[key] = x;
      });

      const list = Object.values(byTenant).sort((a, b) => {
        const an = `${a.tenant_name || ""} ${a.gateway_code || ""}`.toLowerCase();
        const bn = `${b.tenant_name || ""} ${b.gateway_code || ""}`.toLowerCase();
        return an.localeCompare(bn, "tr");
      });

      setItems(list);
      setSelectedId(prev => list.some(x => x.gateway_id === prev) ? prev : (list?.[0]?.gateway_id || null));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(x =>
      `${x.tenant_name || ""} ${x.tenant_subdomain || ""} ${x.gateway_name || ""} ${x.gateway_code || ""} ${x.version || ""}`
        .toLowerCase()
        .includes(s)
    );
  }, [items, q]);

  const selected = filtered.find(x => x.gateway_id === selectedId) || filtered[0] || null;

  useEffect(() => {
    if (selected?.gateway_id) loadCommands(selected.gateway_id);
  }, [selected?.gateway_id]);


  const stats = useMemo(() => ({
    total: items.length,
    online: items.filter(isOnline).length,
    offline: items.filter(x => !isOnline(x)).length,
    machines: items.reduce((a, x) => a + Number(x.machine_count || 0), 0),
    onlineMachines: items.reduce((a, x) => a + Number(x.online_machine_count || 0), 0),
  }), [items]);

  const loadCommands = async (gatewayId) => {
    if (!gatewayId) {
      setCommands([]);
      return;
    }

    try {
      const r = await fetch(`${API}/superadmin/edge-manager/commands?gateway_id=${gatewayId}&limit=10`, {
        headers: headers()
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Komut geçmişi alınamadı");
      setCommands(d.items || []);
    } catch (e) {
      console.error(e);
      setCommands([]);
    }
  };

  const loadCredentials = async () => {
    try {
      setCredentialLoading(true);

      const r = await fetch(
        `${API}/superadmin/edge-manager/credentials`,
        { headers: headers() }
      );

      const d = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(d.detail || "Credential listesi alınamadı");
      }

      setCredentials(d.items || []);
    } catch (e) {
      console.error(e);
      setCredentials([]);
    } finally {
      setCredentialLoading(false);
    }
  };

  const provisionGateway = async () => {
    const tenantId = Number(provisionForm.tenant_id || 0);

    if (
      !tenantId ||
      !provisionForm.gateway_name.trim() ||
      !provisionForm.gateway_code.trim()
    ) {
      window.alert("Tenant, gateway adı ve gateway kodu zorunludur.");
      return;
    }

    try {
      setProvisionSaving(true);

      const r = await fetch(
        `${API}/superadmin/edge-manager/provision`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            tenant_id: tenantId,
            gateway_name: provisionForm.gateway_name.trim(),
            gateway_code: provisionForm.gateway_code.trim(),
            location: provisionForm.location.trim() || null
          })
        }
      );

      const d = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(d.detail || "Gateway provision başarısız");
      }

      setProvisionOpen(false);
      setProvisionForm({
        tenant_id: "",
        gateway_name: "",
        gateway_code: "",
        location: ""
      });

      await load();
    } catch (e) {
      window.alert(e.message || "Gateway provision başarısız");
    } finally {
      setProvisionSaving(false);
    }
  };

  const issueCredential = async (gateway) => {
    if (!gateway) return;

    try {
      const r = await fetch(
        `${API}/superadmin/edge-manager/credentials`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            tenant_id: Number(gateway.tenant_id),
            gateway_code: gateway.gateway_code
          })
        }
      );

      const d = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(d.detail || "Credential oluşturulamadı");
      }

      setSecretModal(d);
      await loadCredentials();
    } catch (e) {
      window.alert(e.message || "Credential oluşturulamadı");
    }
  };

  const rotateCredential = async (credential) => {
    if (!credential) return;

    if (!window.confirm("Aktif credential rotate edilsin mi?")) {
      return;
    }

    try {
      const r = await fetch(
        `${API}/superadmin/edge-manager/credentials/${credential.id}/rotate`,
        {
          method: "POST",
          headers: headers()
        }
      );

      const d = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(d.detail || "Credential rotate edilemedi");
      }

      setSecretModal(d);
      await loadCredentials();
    } catch (e) {
      window.alert(e.message || "Credential rotate edilemedi");
    }
  };

  const revokeCredential = async (credential) => {
    if (!credential) return;

    if (!window.confirm("Credential revoke edilsin mi?")) {
      return;
    }

    try {
      const r = await fetch(
        `${API}/superadmin/edge-manager/credentials/${credential.id}/revoke`,
        {
          method: "POST",
          headers: headers()
        }
      );

      const d = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(d.detail || "Credential revoke edilemedi");
      }

      await loadCredentials();
    } catch (e) {
      window.alert(e.message || "Credential revoke edilemedi");
    }
  };

  const sendCommand = async (command_type) => {
    if (!selected) return;

    const labels = {
      restart_edge: "Restart Edge",
      sync_config: "Sync Config",
      test_connection: "Test Connection",
      download_logs: "Download Logs",
    diagnostics: "Diagnostics",
    collect_snapshot: "Snapshot",
    };

    if (!window.confirm(`${labels[command_type] || command_type} komutu gönderilsin mi?`)) return;

    setCommandBusy(true);
    setCommandMsg("");

    try {
      const r = await fetch(`${API}/superadmin/edge-manager/devices/${selected.gateway_id}/commands`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ command_type })
      });

      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Komut oluşturulamadı");

      setCommandMsg(`Komut kuyruğa alındı (#${d.command_id})`);
      load();
      loadCommands(selected.gateway_id);
    } catch (e) {
      setCommandMsg(e.message || "Komut gönderilemedi");
    }

    setCommandBusy(false);
  };

  return (
    <div style={S.page}>
      <div style={S.titleRow}>
        <div>
          <h2 style={S.title}>🧠 Global Edge Manager</h2>
          <div style={S.sub}>Tüm tenant Edge Gateway bağlantıları, canlı durumları ve güvenli uzaktan yönetim.</div>
        </div>
        <button style={S.btn} onClick={load}>Yenile</button>
      </div>

      <div style={S.grid4}>
        <div style={S.card}><div style={S.kpiLabel}>Toplam Gateway</div><div style={{...S.kpiValue, color:"#60a5fa"}}>{stats.total}</div></div>
        <div style={S.card}><div style={S.kpiLabel}>Online Gateway</div><div style={{...S.kpiValue, color:"#34d399"}}>{stats.online}</div></div>
        <div style={S.card}><div style={S.kpiLabel}>Offline Gateway</div><div style={{...S.kpiValue, color:"#fb7185"}}>{stats.offline}</div></div>
        <div style={S.card}><div style={S.kpiLabel}>Online Makine</div><div style={{...S.kpiValue, color:"#a78bfa"}}>{stats.onlineMachines}/{stats.machines}</div></div>
      </div>

      <div style={S.layout}>
        <div style={S.card}>
          <div style={S.toolbar}>
            <div style={{fontWeight:900}}>Gateway Listesi</div>
            <input style={S.input} value={q} onChange={e=>setQ(e.target.value)} placeholder="Tenant, gateway veya versiyon ara..." />
          </div>

          {loading ? (
            <div>Yükleniyor...</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Tenant</th>
                  <th style={S.th}>Gateway</th>
                  <th style={S.th}>Durum</th>
                  <th style={S.th}>Makine</th>
                  <th style={S.th}>Üretim</th>
                  <th style={S.th}>Son Görülme</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(x => (
                  <tr key={x.gateway_id} style={S.row(selected?.gateway_id === x.gateway_id)} onClick={()=>setSelectedId(x.gateway_id)}>
                    <td style={S.td}>
                      <div style={{fontWeight:900}}>{x.tenant_name}</div>
                      <div style={{fontSize:11, color:"#7b8da8"}}>{x.tenant_subdomain || "—"}</div>
                    </td>
                    <td style={S.td}>
                      <div style={{fontWeight:800}}>{x.gateway_name}</div>
                      <div style={{fontSize:11, color:"#7b8da8"}}>{x.gateway_code}</div>
                    </td>
                    <td style={S.td}><span style={S.pill(isOnline(x))}>{isOnline(x) ? "● Online" : "● Offline"}</span></td>
                    <td style={S.td}>{x.online_machine_count}/{x.machine_count}</td>
                    <td style={S.td}>{x.total_production || 0}</td>
                    <td style={S.td}>{fmtDate(x.gateway_last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={S.card}>
          {selected ? (
            <>
              <div style={S.detailTitle}>{selected.gateway_name}</div>
              <div style={S.detailSub}>{selected.tenant_name} · {selected.gateway_code}</div>

              <div style={S.sectionTitle}>Gateway Health</div>
              <div style={S.kvGrid}>
                <div style={S.kv}><div style={S.k}>Durum</div><div style={S.v}><span style={S.pill(isOnline(selected))}>{isOnline(selected) ? "● Online" : "● Offline"}</span></div></div>
                <div style={S.kv}><div style={S.k}>Hostname</div><div style={S.v}>{selected.hostname || "—"}</div></div>
                <div style={S.kv}><div style={S.k}>CPU Load</div><div style={S.v}>{selected.cpu_load || "—"}</div></div>
                <div style={S.kv}><div style={S.k}>RAM</div><div style={S.v}>{pct(selected.ram_percent)}</div></div>
                <div style={S.kv}><div style={S.k}>Disk</div><div style={S.v}>{pct(selected.disk_percent)}</div></div>
                <div style={S.kv}><div style={S.k}>Uptime</div><div style={S.v}>{fmtUptime(selected.uptime_seconds)}</div></div>
                <div style={S.kv}><div style={S.k}>Queue</div><div style={S.v}>{selected.queue_count ?? 0}</div></div>
                <div style={S.kv}><div style={S.k}>Heartbeat</div><div style={S.v}>{fmtDate(selected.gateway_last_seen)}</div></div>
                <div style={{...S.kv, gridColumn:"1 / -1"}}><div style={S.k}>Platform</div><div style={S.v}>{selected.platform_info || "—"}</div></div>
              </div>

              <div style={S.sectionTitle}>Gateway Information</div>
              <div style={S.kvGrid}>
                <div style={S.kv}><div style={S.k}>Tenant</div><div style={S.v}>{selected.tenant_name}</div></div>
                <div style={S.kv}><div style={S.k}>Gateway</div><div style={S.v}>{selected.gateway_code}</div></div>
                <div style={S.kv}><div style={S.k}>Version</div><div style={S.v}>{selected.version || "—"}</div></div>
                <div style={S.kv}><div style={S.k}>IP</div><div style={S.v}>{selected.ip_address || "—"}</div></div>
                <div style={S.kv}><div style={S.k}>Makine</div><div style={S.v}>{selected.online_machine_count}/{selected.machine_count}</div></div>
                <div style={S.kv}><div style={S.k}>Üretim / Hurda</div><div style={S.v}>{selected.total_production || 0} / {selected.total_defective || 0}</div></div>
                <div style={{...S.kv, gridColumn:"1 / -1"}}><div style={S.k}>Makine Son Veri</div><div style={S.v}>{fmtDate(selected.last_machine_seen)}</div></div>
              </div>

              <div style={S.sectionTitle}>Remote Commands</div>
              <div style={S.commandGrid}>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("test_connection")}>Test Connection</button>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("diagnostics")}>Diagnostics</button>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("collect_snapshot")}>Snapshot</button>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("sync_config")}>Sync Config</button>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("download_logs")}>Download Logs</button>
                <button style={S.btn} disabled={commandBusy} onClick={() => sendCommand("restart_edge")}>Restart Dry Run</button>
              </div>

              <div style={S.log}>
                <div>Gateway ID: {selected.gateway_id}</div>
                <div>Command Queue: edge_commands</div>
                <div>{commandMsg || "Komut göndermek için yukarıdaki butonları kullan."}</div>
              </div>

              <div style={S.sectionTitle}>Command History</div>
              <div style={{background:"#050b13", border:"1px solid #172a46", borderRadius:12, overflow:"hidden"}}>
                {(commands || []).length ? commands.map(c => (
                  <div key={c.id} style={S.historyRow} onClick={() => setSelectedCommand(c)}>
                    <div style={{fontWeight:900}}>{commandLabel(c.command_type)}</div>
                    <div style={{...commandStatusStyle(c.status), fontWeight:900}}>{String(c.status || "—").toUpperCase()}</div>
                    <div style={{color:"#94a3b8"}}>{fmtDate(c.created_at)}</div>
                  </div>
                )) : (
                  <div style={{padding:12, color:"#94a3b8", fontSize:12}}>Henüz komut geçmişi yok.</div>
                )}
              </div>
            </>
          ) : (
            <div>Gateway seçilmedi.</div>
          )}
        </div>
      </div>
      {selectedCommand && (
        <div style={S.modalBackdrop} onClick={() => setSelectedCommand(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:14}}>
              <div>
                <div style={{fontSize:20, fontWeight:900}}>Command Detail</div>
                <div style={{color:"#94a3b8", fontSize:13}}>#{selectedCommand.id} · {commandLabel(selectedCommand.command_type)}</div>
              </div>
              <button style={S.btn} onClick={() => setSelectedCommand(null)}>Kapat</button>
            </div>

            <div style={S.kvGrid}>
              <div style={S.kv}><div style={S.k}>Status</div><div style={{...S.v, ...commandStatusStyle(selectedCommand.status)}}>{String(selectedCommand.status || "—").toUpperCase()}</div></div>
              <div style={S.kv}><div style={S.k}>Requested By</div><div style={S.v}>{selectedCommand.requested_by || "—"}</div></div>
              <div style={S.kv}><div style={S.k}>Created</div><div style={S.v}>{fmtDate(selectedCommand.created_at)}</div></div>
              <div style={S.kv}><div style={S.k}>Picked</div><div style={S.v}>{fmtDate(selectedCommand.picked_at)}</div></div>
              <div style={S.kv}><div style={S.k}>Completed</div><div style={S.v}>{fmtDate(selectedCommand.completed_at)}</div></div>
              <div style={S.kv}><div style={S.k}>Duration</div><div style={S.v}>{fmtDuration(selectedCommand.picked_at, selectedCommand.completed_at)}</div></div>
            </div>

            {selectedCommand.error_message && (
              <>
                <div style={S.sectionTitle}>Error</div>
                <pre style={S.pre}>{selectedCommand.error_message}</pre>
              </>
            )}

            <div style={S.sectionTitle}>Payload JSON</div>
            <pre style={S.pre}>{jsonText(selectedCommand.payload)}</pre>

            <div style={S.sectionTitle}>Result JSON</div>
            <pre style={S.pre}>{jsonText(selectedCommand.result)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
